import '../../scrollytelling.css'
import '../showcase/stage/showcase.css'
import './play.css'

import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useCallback, useMemo, useState } from 'react'

import logo from '../../../../assets/images/logo-hq.png'
import { SceneProvider } from '../../engine'
import { LANGS, StoryProvider, readLang, writeParam } from '../i18n'
import { useShowStory } from '../showcase/stage/engine'
import { Magnetic } from '../showcase/stage/fx'
import { LINKS } from './copy'
import { C, Shape } from './shapes'

const DOT_KINDS = ['tri', 'square', 'circle']
const DOT_COLORS = [C.yellow, C.green, C.pink, C.blue]

/**
 * Rahmen von „Aivy Play“: flache, helle Bühne im Aivy-Look, Navigation mit
 * Formen als Kapitelpunkte und eine CTA-Leiste, die ab dem zweiten Kapitel
 * mitläuft. Die Scroll-Engine ist die der Showcase-Versionen (mit Trägheit).
 * Sections haben `tone` (light, blue, white), `height` in vh oder `flow`.
 */
export default function PlayStory({ sections, copy }) {
  const [lang, setLang] = useState(readLang)

  const choose = (next) => {
    if (next === lang) return
    writeParam('lang', next === 'de' ? null : next)
    setLang(next)
  }

  return (
    <Story
      key={lang}
      sections={sections}
      copy={copy[lang]}
      lang={lang}
      onLang={choose}
    />
  )
}

const sectionShape = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    height: PropTypes.number,
    tone: PropTypes.string,
    flow: PropTypes.bool,
    Scene: PropTypes.elementType.isRequired
  })
)

PlayStory.propTypes = {
  sections: sectionShape.isRequired,
  copy: PropTypes.shape({ de: PropTypes.object, en: PropTypes.object })
    .isRequired
}

/** Die beiden Handlungsaufforderungen, überall gleich. */
export function CtaPair({ ui, className, size, onDark }) {
  return (
    <div className={clsx('ap-ctas', size && `ap-ctas--${size}`, className)}>
      <Magnetic
        href={LINKS.demo}
        target='_blank'
        rel='noreferrer'
        className={clsx('ap-btn', onDark ? 'ap-btn--white' : 'ap-btn--blue')}
      >
        {ui.demo}
        <Shape
          kind='tri'
          color='currentColor'
          size={12}
          className='ap-btn__tri'
        />
      </Magnetic>
      <Magnetic
        href={LINKS.start}
        className={clsx(
          'ap-btn',
          onDark ? 'ap-btn--line-white' : 'ap-btn--line'
        )}
      >
        {ui.start}
      </Magnetic>
    </div>
  )
}

CtaPair.propTypes = {
  ui: PropTypes.object.isRequired,
  className: PropTypes.string,
  size: PropTypes.string,
  onDark: PropTypes.bool
}

function Story({ sections, copy, lang, onLang }) {
  const story = useShowStory(sections.length)
  const { scrollToChapter, active } = story
  const current = sections[active]

  const goTo = useCallback(
    (id) => {
      const index = sections.findIndex((s) => s.id === id)
      if (index >= 0) scrollToChapter(index)
    },
    [sections, scrollToChapter]
  )
  const value = useMemo(
    () => ({ lang, copy, goTo, theme: 'light' }),
    [lang, copy, goTo]
  )
  const dock = active > 0 && active < sections.length - 1

  return (
    <StoryProvider value={value}>
      <div
        ref={story.storyRef}
        lang={lang}
        className={clsx(
          'sty-root ap-root',
          `ap-on--${current.tone || 'light'}`
        )}
      >
        <div ref={story.progressRef} className='ap-top'>
          <nav className='ap-nav' aria-label={copy.ui.chapters}>
            <button
              type='button'
              className='ap-nav__logo'
              onClick={() => scrollToChapter(0)}
              aria-label='Aivy'
            >
              <img src={logo} alt='' />
            </button>
            <span className='ap-nav__dots'>
              {sections.map((s, i) => (
                <button
                  key={s.id}
                  type='button'
                  className={clsx('ap-nav__dot', i === active && 'is-active')}
                  onClick={() => scrollToChapter(i)}
                  aria-label={copy.chapters[s.id]}
                  title={copy.chapters[s.id]}
                >
                  <Shape
                    kind={DOT_KINDS[i % 3]}
                    color={i <= active ? DOT_COLORS[i % 4] : '#C9D0DC'}
                    size={14}
                  />
                </button>
              ))}
            </span>
            <span className='ap-nav__label'>{copy.chapters[current.id]}</span>
            <span className='ap-lang' role='group' aria-label='Sprache'>
              {LANGS.map((l) => (
                <button
                  key={l}
                  type='button'
                  className={clsx('ap-lang__btn', l === lang && 'is-active')}
                  aria-pressed={l === lang}
                  onClick={() => onLang(l)}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </span>
            <a
              className='ap-nav__cta'
              href={LINKS.demo}
              target='_blank'
              rel='noreferrer'
            >
              {copy.ui.demo}
            </a>
          </nav>
          <div className='ap-top__bar' />
        </div>

        <div className={clsx('ap-dock', dock && 'is-on')} aria-hidden={!dock}>
          <span className='ap-dock__text'>{copy.ui.dock}</span>
          <CtaPair ui={copy.ui} size='sm' onDark />
        </div>

        {sections.map(({ id, height, tone = 'light', flow, Scene }, i) => (
          <section
            key={id}
            id={`ap-${id}`}
            ref={(el) => (story.chapterRefs.current[i] = el)}
            className={clsx(
              'ap-section',
              `ap-tone--${tone}`,
              flow && 'ap-flow'
            )}
            style={flow ? undefined : { height: `${height}vh` }}
            aria-label={copy.chapters[id]}
          >
            <div
              ref={(el) => (story.sceneRefs.current[i] = el)}
              className={flow ? 'ap-flow__inner' : 'ap-sticky'}
            >
              <SceneProvider index={i} subscribe={story.subscribe}>
                <Scene />
              </SceneProvider>
            </div>
          </section>
        ))}
      </div>
    </StoryProvider>
  )
}

Story.propTypes = {
  sections: sectionShape.isRequired,
  copy: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  onLang: PropTypes.func.isRequired
}
